/** Typed fetch wrapper.
 *
 *  The access token is held in memory only — never localStorage, which is readable by
 *  any injected script. The refresh token is an httpOnly cookie the browser sends on
 *  its own. A 401 triggers a single refresh attempt, and concurrent 401s share it.
 */
import type { ApiError } from '@/types'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

let accessToken: string | null = null
let refreshPromise: Promise<boolean> | null = null
let onSessionLost: (() => void) | null = null

export const setAccessToken = (token: string | null) => { accessToken = token }
export const getAccessToken = () => accessToken
export const onSessionExpired = (handler: () => void) => { onSessionLost = handler }

export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number
  readonly details: Record<string, unknown>
  readonly requestId?: string

  constructor(status: number, error: ApiError) {
    super(error.message)
    this.name = 'ApiRequestError'
    this.code = error.code
    this.status = status
    this.details = error.details ?? {}
    this.requestId = error.request_id
  }
}

const OFFLINE_ERROR: ApiError = {
  code: 'NETWORK_UNAVAILABLE',
  message: 'No connection. Your work is saved on this device — retry when you have signal.',
}

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) return false
        const body = await response.json()
        setAccessToken(body.data.access_token)
        return true
      })
      .catch(() => false)
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  skipAuthRetry?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuthRetry, headers, ...rest } = options

  const send = async (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

  let response: Response
  try {
    response = await send()
  } catch {
    throw new ApiRequestError(0, OFFLINE_ERROR)
  }

  if (response.status === 401 && !skipAuthRetry) {
    if (await refreshSession()) {
      response = await send()
    } else {
      onSessionLost?.()
    }
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new ApiRequestError(
      response.status,
      payload?.error ?? { code: 'INTERNAL_ERROR', message: 'Something went wrong.' },
    )
  }
  return payload.data as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: 'POST', body, headers }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Raw PUT used for the direct-to-storage upload. Deliberately not wrapped in the
 *  envelope helpers: the target is S3, not this API. */
export async function uploadBytes(
  url: string,
  blob: Blob,
  headers: Record<string, string>,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value))
    if (url.startsWith('/')) xhr.withCredentials = true
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiRequestError(xhr.status, { code: 'UPLOAD_FAILED', message: 'Upload failed.' }))
    xhr.onerror = () => reject(new ApiRequestError(0, OFFLINE_ERROR))
    xhr.send(blob)
  })
}
